import { type ChangeEvent, type ReactNode } from 'react';
import { token } from '@/theme';
import { UnderlineCellContainer, BandConfigurationContainer } from './styled';
import type { AlignType } from 'rc-table/lib/interface';
import { Tooltip } from 'antd';
import { EPSelect, EPSelectOption } from '@/components';
import { bandConfigurationOptions, LONG_DASH } from '../EngineVersionUtils';
import { NewFallbackColumnsNameEnum } from '@/constants/FallbackEnum';
import { Setpoint } from '@/store/Engine/common';
import { UnderlineCell } from './columnsUnderLineCell';
import { isEmptyValue, renderValue } from './columns.utils';


export const getColumns = (
  isEditable: boolean,
  t: (key: string) => string,
  handleCellChange?: (key: string | number, field: keyof Setpoint, value) => void,
  compressorTooltipContents?: Record<string, ReactNode>,
) => {
  const createCellChangeHandler = (record: Setpoint, field: keyof Setpoint) => value => {
    handleCellChange?.(record.key, field, value);
  };

  return [
    {
      title: t('no'),
      dataIndex: 'compressorNumber',
      key: 'compressorNumber',
      width: 60,
      render: (text: number) => renderValue(text, token.colorText, false),
    },
    {
      title: t('Compressor'),
      dataIndex: 'compressorName',
      key: 'compressorName',
      width: 300,
      render: (text: string) => renderValue(text, token.colorText, false),
    },
    {
      title: t('titleOrder'),
      dataIndex: NewFallbackColumnsNameEnum.Order,
      key: NewFallbackColumnsNameEnum.Order,
      width: 80,
      align: 'center' as AlignType,
      render: isEditable
        ? (text, record: Setpoint) => (
            <UnderlineCell
              value={record.order}
              onChange={createCellChangeHandler(record, NewFallbackColumnsNameEnum.Order)}
              centerAlign={true}
            />
          )
        : (text: number | string) => renderValue(text, token.colorText, true),
    },
    {
      title: t('titleLoad'),
      dataIndex: NewFallbackColumnsNameEnum.Load,
      key: NewFallbackColumnsNameEnum.Load,
      width: 80,
      align: 'center' as AlignType,
      render: isEditable
        ? (text, record: Setpoint) => (
            <UnderlineCell
              value={record.load}
              onChange={createCellChangeHandler(record, NewFallbackColumnsNameEnum.Load)}
              centerAlign={true}
            />
          )
        : (text: number | string) => renderValue(text, token.colorText, true),
    },
    {
      title: t('titleTarget'),
      dataIndex: NewFallbackColumnsNameEnum.VsdTarget,
      key: NewFallbackColumnsNameEnum.VsdTarget,
      width: 80,
      align: 'center' as AlignType,
      render: isEditable
        ? (text, record: Setpoint) => (
            <UnderlineCell
              value={record.vsdTarget}
              onChange={createCellChangeHandler(record, NewFallbackColumnsNameEnum.VsdTarget)}
              centerAlign={true}
            />
          )
        : (text: number | string) => renderValue(text, token.colorText, true),
    },
    {
      title: t('titleUnload'),
      dataIndex: NewFallbackColumnsNameEnum.Unload,
      key: NewFallbackColumnsNameEnum.Unload,
      width: 80,
      align: 'center' as AlignType,
      render: isEditable
        ? (text, record: Setpoint) => (
            <UnderlineCell
              value={record.unload}
              onChange={createCellChangeHandler(record, NewFallbackColumnsNameEnum.Unload)}
              centerAlign={true}
            />
          )
        : (text: number | string) => renderValue(text, token.colorText, true),
    },
    {
      title: t('titleAutoStop'),
      dataIndex: NewFallbackColumnsNameEnum.AutoStop,
      key: NewFallbackColumnsNameEnum.AutoStop,
      width: 100,
      align: 'center' as AlignType,
      render: isEditable
        ? (text: string, record: Setpoint) => (
            <UnderlineCell
              value={record.autoStop}
              onChange={createCellChangeHandler(record, NewFallbackColumnsNameEnum.AutoStop)}
              centerAlign={true}
            />
          )
        : (text: string) => renderValue(text, token.colorText, true),
    },
    {
      title: t('titleImmediateStop'),
      dataIndex: NewFallbackColumnsNameEnum.ImmediateStop,
      key: NewFallbackColumnsNameEnum.ImmediateStop,
      width: 100,
      align: 'center' as AlignType,
      render: isEditable
        ? (text: string, record: Setpoint) => (
            <UnderlineCell
              value={record.immediateStop}
              onChange={createCellChangeHandler(record, NewFallbackColumnsNameEnum.ImmediateStop)}
              centerAlign={true}
            />
          )
        : (text: string) => renderValue(text, token.colorText, true),
    },
    {
      title: t('titlePressureDrop'),
      dataIndex: NewFallbackColumnsNameEnum.PressureDrop,
      key: NewFallbackColumnsNameEnum.PressureDrop,
      width: 100,
      align: 'center' as AlignType,
      render: isEditable
        ? (text: string, record: Setpoint) => (
            <UnderlineCell
              value={record.pressureDrop}
              onChange={createCellChangeHandler(record, NewFallbackColumnsNameEnum.PressureDrop)}
              centerAlign={true}
            />
          )
        : (text: string) => renderValue(text, token.colorText, true),
    },
    {
      title: t('titleModulate'),
      dataIndex: NewFallbackColumnsNameEnum.Modulate,
      key: NewFallbackColumnsNameEnum.Modulate,
      width: 100,
      align: 'center' as AlignType,
      render: isEditable
        ? (value, record: Setpoint) => {
            if (record.modulate === null || record.modulate === undefined) {
              return (
                <UnderlineCellContainer $centerAlign={true}>
                  <span className="empty-value">{LONG_DASH}</span>
                </UnderlineCellContainer>
              );
            }
            return (
              <UnderlineCellContainer $centerAlign={true}>
                <input
                  className="draft-modulate-checkbox"
                  type="checkbox"
                  checked={!!record.modulate}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    createCellChangeHandler(record, NewFallbackColumnsNameEnum.Modulate)(e.target.checked)
                  }
                />
              </UnderlineCellContainer>
            );
          }
        : (modulate: boolean | string) => {
            if (modulate === '') {
              return renderValue(modulate, token.colorText, true) || LONG_DASH;
            }
            if (typeof modulate === 'boolean') {
              return (
                <UnderlineCellContainer $accentColor={token.colorPrimary} $centerAlign={true}>
                  <input className="modulate-checkbox" type="checkbox" checked={!!modulate} readOnly />
                </UnderlineCellContainer>
              );
            }
          },
    },
    {
      title: t('titleBandConfiguration'),
      dataIndex: NewFallbackColumnsNameEnum.BandConfiguration,
      key: NewFallbackColumnsNameEnum.BandConfiguration,
      width: 100,
      align: 'center' as AlignType,
      render: isEditable
        ? (text, record: Setpoint) => {
            const tooltipContent = compressorTooltipContents?.[record.compressorId] || t('noMetricsAvailable');
            const isEmpty = isEmptyValue(record.bandConfiguration);

            const renderSelectOptions = () => {
              return bandConfigurationOptions.map(option => (
                <EPSelectOption value={option} key={option}>
                  {option !== LONG_DASH ? (
                    <Tooltip title={tooltipContent} placement="right">
                      <span>{option}</span>
                    </Tooltip>
                  ) : (
                    <span>{option}</span>
                  )}
                </EPSelectOption>
              ));
            };

            return (
              <BandConfigurationContainer>
                {isEmpty ? (
                  <Tooltip title={tooltipContent} placement="right">
                    <UnderlineCellContainer $hasOnChange={true} $centerAlign={true}>
                      <span className="empty-value">{LONG_DASH}</span>
                    </UnderlineCellContainer>
                  </Tooltip>
                ) : (
                  <EPSelect
                    value={record.bandConfiguration ?? LONG_DASH}
                    onChange={createCellChangeHandler(record, NewFallbackColumnsNameEnum.BandConfiguration)}
                    size="small"
                  >
                    {renderSelectOptions()}
                  </EPSelect>
                )}
              </BandConfigurationContainer>
            );
          }
        : (text: string, record: Setpoint) => {
            const tooltipContent = compressorTooltipContents?.[record.compressorId] || t('noMetricsAvailable');
            const isEmpty = isEmptyValue(text);

            if (isEmpty) {
              return renderValue(text, token.colorText, true) || LONG_DASH;
            }

            return (
              <Tooltip title={tooltipContent} placement="right">
                <UnderlineCellContainer $color={token.colorText} $centerAlign={true}>
                  <span className="value-span">{text}</span>
                </UnderlineCellContainer>
              </Tooltip>
            );
          },
    },
  ];
};
